import React, { useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation, useParams } from 'react-router-dom';
import { dropUnverifiedSession } from './components/auth/pendingSignup';
import { deviceId, withDeviceHeader } from './platform/deviceId';
import { FairPlayStripByCode } from './components/fairplay/FairPlay';
import DuelStreamPanel from './components/arena/DuelStreamPanel';
import { TVChartRoutes } from './pages/duelChartEngineContext';
import { ProgressProvider } from './components/ProgressContext';
import './App.css';
import './i18n';
import VisitorChat from './components/VisitorChat';
import { useTracker } from './hooks/useTracker';
import axios from 'axios';
import SubscriptionGuard from './components/SubscriptionGuard';
import NummiusLoader from './components/NummiusLoader';
import AssistantDock, { AssistantDockProvider } from './components/assistant/AssistantDock';
import { academyPathFromLegacy, afterLoginPath, isAcademySite, expiredSessionPath, siteOrigins } from './site';
import RouteMeta from './components/RouteMeta';
import ViewAsBar from './platform/ViewAsBar';
import NotificationPopups from './components/NotificationPopups';
import { exitViewAs, viewAs } from './platform/viewAs';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────
const Dashboard = React.lazy(() => import('./Dashboard'));
const ScriptGuidePage = React.lazy(() => import('./pages/ScriptGuidePage'));
const Login = React.lazy(() => import('./Login'));
const HomePage = React.lazy(() => import('./marketing/HomePage'));
const FeaturesPage = React.lazy(() => import('./marketing/FeaturesPage'));
const AboutPage = React.lazy(() => import('./marketing/AboutPage'));
const PricingPage = React.lazy(() => import('./marketing/PricingPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const BillingSuccess = React.lazy(() => import('./pages/BillingSuccess'));
const Register = React.lazy(() => import('./Register'));
const VerifyEmailPage = React.lazy(() => import('./pages/VerifyEmailPage'));
const EmailChangePage = React.lazy(() => import('./pages/EmailChangePage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const PartnersPage = React.lazy(() => import('./pages/PartnersPage'));
const PrivacyPage = React.lazy(() => import('./marketing/PrivacyPage'));
const AndroidPage = React.lazy(() => import('./marketing/AndroidPage'));
const TermsPage = React.lazy(() => import('./marketing/TermsPage'));
const AffiliateDashboard = React.lazy(() => import('./pages/AffiliateDashboard'));
const ChallengeWeekPage = React.lazy(() => import('./pages/ChallengeWeekPage'));
const ChallengeResumePage = React.lazy(() => import('./pages/ChallengeResumePage'));
const ChallengeLeaderboardPage = React.lazy(() => import('./pages/ChallengeLeaderboardPage'));
const DuelSpectatePage = React.lazy(() => import('./pages/DuelSpectatePage'));
// Arena redesign (2026-10, pages/arena/): the duel room, the invite, the open board, the leaderboard.
const DuelRoomPage = React.lazy(() => import('./pages/arena/DuelRoomPage'));
const InvitePage = React.lazy(() => import('./pages/arena/InvitePage'));
const OpenBoardPage = React.lazy(() => import('./pages/arena/OpenBoardPage'));
const LeaderboardPage = React.lazy(() => import('./pages/arena/LeaderboardPage'));
const TournamentsPage = React.lazy(() => import('./pages/practice/TournamentsPage'));
const PracticePage = React.lazy(() => import('./pages/practice/PracticePage'));
const DuelPeekabooPage = React.lazy(() => import('./pages/DuelPeekabooPage'));
const BotExhibitionPage = React.lazy(() => import('./pages/BotExhibitionPage'));
const DailyChallengeScreenPage = React.lazy(() => import('./pages/DailyChallengeScreenPage'));
const DuelProfilePage = React.lazy(() => import('./pages/DuelProfilePage'));
const DuelPublicProfilePage = React.lazy(() => import('./pages/DuelPublicProfilePage'));
// TradingView-engine test build of the Arena games (see pages/TVLabPage.jsx).
const WeeklyRecap = React.lazy(() => import('./components/recap/WeeklyRecap'));
// Nummius Academy lives on its own site (academy.nummius.com, same build): site.js tells which host this is.
const AcademySite = React.lazy(() => import('./edu/site/AcademySite'));
const TVLabPage = React.lazy(() => import('./pages/TVLabPage'));
const CardPreviewPage = React.lazy(() => import('./pages/CardPreviewPage'));
const NotFoundPage = React.lazy(() => import('./pages/NotFoundPage'));
const PublicProfilePage = React.lazy(() => import('./pages/profile/PublicProfilePage'));



// Lazy route chunks: full-viewport brand loader (same one index.html shows
// before the bundle loads, so boot -> first route is one continuous loader).
const PageLoader = () => <NummiusLoader variant="page" label="Loading page" />;

// Fair play: the "under review / excluded" strip over a public duel page reached by invite code.
function WithFairPlay({ children }) {
  const { inviteCode } = useParams();
  return <>{inviteCode && <FairPlayStripByCode code={inviteCode} />}{children}</>;
}

// <Navigate> for a path built from the current route's params.
function ParamRedirect({ to }) {
  return <Navigate to={to(useParams())} replace />;
}

// Old practice entry points -> the Arena home, keeping their query. The Arena feed
// (/practice/arena) was folded into the home in the 2026-10 redesign; its tabs map to:
//   all, duels -> the Arena home   rounds, platform -> Tournaments   leaderboard -> /arena/leaderboard
//   /arena                    -> /practice?tab=compete
//   /duels/challenge?mode=x   -> /practice?tab=compete&mode=x
const FEED_TAB_DEST = { rounds: '/practice/tournaments', platform: '/practice/tournaments', leaderboard: '/arena/leaderboard' };
const FEED_TAB_BY_LABEL = { 'Creator Rounds': 'rounds', Platform: 'platform', Leaderboard: 'leaderboard' };
function ArenaRedirect() {
  const { search, state } = useLocation();
  const tab = new URLSearchParams(search).get('tab') || FEED_TAB_BY_LABEL[state?.tab];
  return <Navigate to={FEED_TAB_DEST[tab] || '/practice?tab=compete'} replace />;
}
function DuelChallengeRedirect() {
  const mode = new URLSearchParams(useLocation().search).get('mode');
  return <Navigate to={`/practice?tab=compete${mode ? `&mode=${encodeURIComponent(mode)}` : ''}`} replace />;
}

// /@handle: React Router can't match a partial segment, so match one segment
// and only treat it as a profile when it starts with '@'.
function AtHandleRoute() {
  const { seg } = useParams();
  return seg && seg.startsWith('@') && seg.length > 1 ? <PublicProfilePage handle={seg.slice(1)} /> : <NotFoundPage />;
}

// nummius.com side of the Academy move: send the visitor to the same page on academy.nummius.com.
function GoToAcademy() {
  const { pathname, search, hash } = useLocation();
  const { academy } = siteOrigins();
  React.useEffect(() => {
    if (academy) window.location.replace(academy + academyPathFromLegacy(pathname, search, hash));
  }, [academy, pathname, search, hash]);
  return academy ? <PageLoader /> : <NotFoundPage />;
}

function AppInner() {
  // Persisted under the same key InvestmentsGrid used before the shared
  // Header picked up the account picker, so an existing saved choice carries
  // over - and now it follows across every /app/* page, not just that one.
  const [selectedAccount, setSelectedAccount] = useState(() => localStorage.getItem('invDash.account') || 'All');
  React.useEffect(() => { localStorage.setItem('invDash.account', selectedAccount); }, [selectedAccount]);
  const isLoggedIn = !!localStorage.getItem('token');
  const location = useLocation();

  React.useEffect(function() {
    // Redirect to login when the session token is missing/expired/invalid (401),
    // no matter which request path (axios OR raw fetch) hit it.
    function handleExpiredSession() {
      // A 401 with no token present is expected guest behavior on
      // account-gated endpoints (e.g. duel end-screen claim stats) - not
      // an expired session. Only redirect if there WAS a real session
      // that went stale.
      const token = localStorage.getItem('token');
      if (!token) return;
      if (viewAs()) { exitViewAs(); return; } // a God's Eye "view as" session ran out: back to your own account
      if (!window.location.pathname.startsWith('/login')) {
        localStorage.removeItem('token');
        window.location.href = expiredSessionPath(token); // back to this page after logging in (a finished demo: sign up)
      }
    }

    const interceptor = axios.interceptors.response.use(
      function(response) { return response; },
      function(error) {
        if (error.response && error.response.status === 402) {
          if (!window.location.pathname.startsWith('/app/billing')) {
            window.location.href = '/app/billing';
          }
        }
        if (error.response && error.response.status === 401) {
          handleExpiredSession();
        }
        if (error.response && error.response.status === 403 && error.response.headers && error.response.headers['x-email-not-verified']) {
          dropUnverifiedSession(); // email not confirmed (backend email_gate.py): guest on Arena, else log in
        }
        if (error.response && error.response.status === 403 && error.response.data && error.response.data.code === 'read_only') {
          window.dispatchEvent(new Event('nm-read-only')); // ViewAsBar: "Blocked: viewing is read-only"
        }
        return Promise.reject(error);
      }
    );

    // Most pages use raw fetch(), not axios - wrap window.fetch once globally
    // so a 401 from ANY request path triggers the same redirect, instead of
    // silently leaving stale/broken UI on pages that don't use axios.
    if (!window.__fetch401WrapperInstalled) {
      window.__fetch401WrapperInstalled = true;
      const nativeFetch = window.fetch.bind(window);
      axios.defaults.headers.common['X-Device-Id'] = deviceId(); // fair play: device ID on every API call
      window.fetch = function(input, init) {
        return nativeFetch(...withDeviceHeader(input, init)).then(function(response) {
          if (response.status === 401) {
            handleExpiredSession();
          }
          if (response.status === 403 && response.headers.get('X-Email-Not-Verified')) {
            dropUnverifiedSession();
          }
          if (response.status === 403 && viewAs()) {
            window.dispatchEvent(new Event('nm-read-only'));
          }
          return response;
        });
      };
    }

    // A session whose email isn't confirmed (signed up before the fix, or older): find out right away.
    if (localStorage.getItem('token') && !viewAs()) {
      fetch(`${process.env.REACT_APP_API_BASE || 'http://localhost:5000'}/api/auth/status`, {
        headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
      }).then((r) => (r.ok ? r.json() : null)).then((s) => { if (s && s.verified === false) dropUnverifiedSession(); }).catch(() => {});
    }

    return function() { axios.interceptors.response.eject(interceptor); };
  }, []);

  useTracker();

  // Login, sign-up and password pages: the same on both sites (one shared login, sharedSession.js).
  const authRoutes = (
    <>
      <Route path="/login"    element={isLoggedIn ? <Navigate to={afterLoginPath()} replace /> : <Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/email-change" element={<EmailChangePage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
    </>
  );

  return (
    <ProgressProvider>
      {/* Visitors who are not logged in get our own support chat (SUP-03, public/nm-chat.js, also on the marketing
          pages); logged in, the Plutus + Support dock (AssistantDock, below) takes over. */}
      {!isLoggedIn && <VisitorChat />}
      <ViewAsBar />
      {/* New notifications (rematches, invites, alerts) pop up on any page, logged in. */}
      {isLoggedIn && <NotificationPopups />}
      {/* Plutus + Support side panel (DOCK-01) for every logged-in /app/* page; it follows the user between pages. */}
      <AssistantDockProvider enabled={isLoggedIn && /^\/app(\/|$)/.test(location.pathname)}>
      <AssistantDock />
      {/* Per-page <title>, description and canonical for the public pages (SEO, components/RouteMeta.jsx). */}
      {!isAcademySite() && <RouteMeta />}
      <React.Suspense fallback={<PageLoader />}>
      {isAcademySite() ? (
        <Routes>
          {authRoutes}
          <Route path="/*" element={<AcademySite isLoggedIn={isLoggedIn} />} />
        </Routes>
      ) : (
      <Routes>
        <Route path="/"         element={<HomePage />} />
        <Route path="/features" element={<FeaturesPage />} />
        <Route path="/about"    element={<AboutPage />} />
        <Route path="/pricing"  element={<PricingPage />} />
        {/* Public Nova Script language guide (linked from the script editor's "Guide ↗"). */}
        <Route path="/docs/script" element={<ScriptGuidePage />} />
        {/* The Academy moved to academy.nummius.com: its share pages and old /app/edu links forward there. */}
        <Route path="/cert/:id" element={<GoToAcademy />} />
        <Route path="/card/:id" element={<GoToAcademy />} />
        <Route path="/record/:id" element={<GoToAcademy />} />
        <Route path="/app/edu/*" element={<GoToAcademy />} />
        {authRoutes}
        <Route path="/partners"  element={<PartnersPage />} />
        <Route path="/privacy"   element={<PrivacyPage />} />
        <Route path="/android"   element={<AndroidPage />} />
        <Route path="/download"  element={<AndroidPage />} />
        <Route path="/terms"     element={<TermsPage />} />
        <Route path="/app/billing"         element={isLoggedIn ? <BillingPage /> : <Navigate to="/login" />} />
        <Route path="/app/billing/success" element={isLoggedIn ? <BillingSuccess /> : <Navigate to="/login" />} />
        <Route path="/app/affiliate" element={isLoggedIn ? <AffiliateDashboard /> : <Navigate to="/login" />} />
        {/* Every Arena game/challenge screen renders on the TradingView
            (lightweight-charts) engine - see pages/duelChartEngine.js. */}
        <Route element={<TVChartRoutes />}>
          <Route path="/challenge/resume/:resumeToken" element={<ChallengeResumePage />} />
          <Route path="/challenge/:weekId/leaderboard" element={<ChallengeLeaderboardPage />} />
          <Route path="/challenge/:weekId" element={<ChallengeWeekPage />} />
          <Route path="/duel/:inviteCode" element={<InvitePage />} />
          <Route path="/duel/:inviteCode/spectate" element={<WithFairPlay><DuelSpectatePage /><DuelStreamPanel /></WithFairPlay>} />
          <Route path="/duel/:inviteCode/peekabu" element={<WithFairPlay><DuelPeekabooPage /></WithFairPlay>} />
          {/* Trading Arena's own public growth-hook routes - deliberately NOT
              under /app/* (which redirects anonymous visitors to /login
              before SubscriptionGuard even runs). These need to work without
              an account, same as /duel/:inviteCode and /challenge/:weekId
              above; each page handles its own login-required moments
              (accepting a duel, saving a result, etc.) inline. */}
          <Route path="/practice" element={<PracticePage />} />
          <Route path="/practice/arena" element={<ArenaRedirect />} />
          <Route path="/arena" element={<ArenaRedirect />} />
          <Route path="/arena/board" element={<OpenBoardPage />} />
          <Route path="/arena/leaderboard" element={<LeaderboardPage />} />
          <Route path="/practice/tournaments" element={<TournamentsPage />} />
          {/* The old "Join or host?" chooser - its links now live on the Tournaments page. */}
          <Route path="/arena/tournaments" element={<Navigate to="/practice/tournaments" replace />} />
          <Route path="/duels/challenge" element={<DuelChallengeRedirect />} />
          <Route path="/duels/daily-challenge/play" element={<DailyChallengeScreenPage />} />
          <Route path="/duels/exhibition/:exhibitionId" element={<BotExhibitionPage />} />
          {/* One duel room address: /duels/:id. The old /live link redirects (the game itself is unchanged). */}
          <Route path="/duels/:duelId" element={<DuelRoomPage />} />
          <Route path="/duels/:duelId/live" element={<ParamRedirect to={(p) => `/duels/${p.duelId}`} />} />
          <Route path="/duels/:duelId/live/peekabu" element={<DuelPeekabooPage />} />
          <Route path="/duels/profile" element={<DuelProfilePage />} />
          <Route path="/duels/players/:username" element={<DuelPublicProfilePage />} />
          {/* Duel Trades is a tab of the Profile now. */}
          <Route path="/duels/backtesting" element={<Navigate to="/duels/profile?tab=trades" replace />} />
          <Route path="/duels/tv" element={<TVLabPage />} />
          {/* Old TradingView test links -> the real routes. */}
          <Route path="/duels/tv/:duelId/live" element={<ParamRedirect to={(p) => `/duels/${p.duelId}`} />} />
          <Route path="/duels/tv/invite/:inviteCode" element={<ParamRedirect to={(p) => `/duel/${p.inviteCode}`} />} />
        </Route>
        {/* Dead /app paths that still get traffic (tracker, 2026-09) - they
            used to render an empty shell. Matched before /app/* so they
            redirect ahead of the login/paywall checks. */}
        <Route path="/app/arena"   element={<Navigate to="/practice" replace />} />
        <Route path="/app/trading" element={<Navigate to="/app" replace />} />
        <Route path="/app/backend" element={<Navigate to="/app" replace />} />
        <Route path="/app/recap" element={isLoggedIn ? <SubscriptionGuard><WeeklyRecap /></SubscriptionGuard> : <Navigate to="/login?next=%2Fapp%2Frecap" />} />
        <Route path="/app/u/:handle" element={isLoggedIn ? <PublicProfilePage /> : <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} />} />
        <Route path="/app/*" element={
          isLoggedIn
            ? <SubscriptionGuard><Dashboard selectedAccount={selectedAccount} onAccountChange={setSelectedAccount} /></SubscriptionGuard>
            // Preserve the intended destination (e.g. a deep link straight
            // to /app/challenges/request-live) through the login round trip
            // - Login.js's getSafeNextPath() already expects this exact
            // ?next=<same-origin path> contract.
            : <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} />
        } />
        <Route path="/dev/card-preview" element={<CardPreviewPage />} />
        {/* Genuinely unmatched top-level paths (e.g. /challenges - note the
            plural, every real route is singular /challenge/...) used to
            silently Navigate to "/app" or "/" depending on login state with
            no indication anything was wrong. A real 404 instead - /app/*
            above already owns everything under /app/ on its own, so this
            only ever catches paths outside that. */}
        <Route path="/:seg" element={<AtHandleRoute />} />
        <Route path="/*" element={<NotFoundPage />} />
      </Routes>
      )}
      </React.Suspense>
      </AssistantDockProvider>
    </ProgressProvider>
  );
}

function App() {
  return (
    <Router>
      <AppInner />
    </Router>
  );
}

export default App;
